import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Search, X, CalendarDays, Plane } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { carModel } from "@/lib/vehicle";
import CarCard from "@/components/CarCard";
import { DateField } from "@/components/DatePickers";
import { Seo } from "@/components/Seo";

const TIMES = ["8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM"];
const today = format(new Date(), "yyyy-MM-dd");

export default function Fleet() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [allCars, setAllCars] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [startTime, setStartTime] = useState("10:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [loc, setLoc] = useState("");
  const [filtered, setFiltered] = useState(false);
  const [make, setMake] = useState("All");
  const [model, setModel] = useState("All");
  const [airportOnly, setAirportOnly] = useState(searchParams.get("airport") === "1");
  const [sort, setSort] = useState(searchParams.get("sort") === "near" ? "near" : "price_asc");
  const [userPos, setUserPos] = useState(null);
  const [geoState, setGeoState] = useState("");

  const fetchCars = (params) => {
    setLoading(true);
    api.get("/cars", { params })
      .then((res) => setAllCars(res.data))
      .catch(() => setAllCars([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const s = searchParams.get("start");
    const e = searchParams.get("end");
    const st = searchParams.get("st");
    const et = searchParams.get("et");
    const lc = searchParams.get("loc");
    const cat = searchParams.get("cat");
    if (lc) setLoc(lc);
    if (cat) setActive(cat);
    if (s && e && e > s) {
      setStart(s); setEnd(e);
      if (st) setStartTime(st);
      if (et) setEndTime(et);
      fetchCars({ start: s, end: e });
      setFiltered(true);
    } else {
      fetchCars();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAvailability = () => {
    if (!start || !end) return;
    fetchCars({ start, end });
    setFiltered(true);
  };

  const clearFilter = () => {
    setStart(""); setEnd(""); setFiltered(false); setActive("All"); setMake("All"); setModel("All");
    fetchCars();
  };

  const canSearch = start && end && end > start;

  // Reset the model when make/category changes so stale selections don't hide all cars.
  useEffect(() => { setModel("All"); }, [make]);
  useEffect(() => { setMake("All"); setModel("All"); }, [active]);

  const categories = useMemo(() => {
    const set = [...new Set(allCars.map((c) => c.category))].filter((c) => c && c !== "Host");
    return ["All", ...set];
  }, [allCars]);

  const carPrice = (c) => c.rates?.daily_start || (c.weekly_rate ? c.weekly_rate / 7 : 0);
  const makes = useMemo(() => {
    const src = active === "All" ? allCars : allCars.filter((c) => c.category === active);
    return ["All", ...[...new Set(src.map((c) => c.make))].filter(Boolean).sort()];
  }, [allCars, active]);
  const models = useMemo(() => {
    const src = allCars.filter((c) => (active === "All" || c.category === active) && (make === "All" || c.make === make));
    return ["All", ...[...new Set(src.map(carModel))].filter(Boolean).sort()];
  }, [allCars, active, make]);
  const distTo = (c) => {
    const co = c.location_coords;
    if (!userPos || !co || co.lat == null) return Infinity;
    const R = 3958.8, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(co.lat - userPos.lat), dLng = toRad(co.lng - userPos.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(userPos.lat)) * Math.cos(toRad(co.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Ask for the visitor's location when "Nearest" sorting is active.
  useEffect(() => {
    if (sort !== "near" || userPos || geoState === "denied") return;
    if (!navigator.geolocation) { setGeoState("unavailable"); return; }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (p) => { setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setGeoState("ok"); },
      () => setGeoState("denied"),
      { timeout: 8000 }
    );
  }, [sort, userPos, geoState]);

  const cars = useMemo(() => {
    let list = active === "All" ? allCars : allCars.filter((c) => c.category === active);
    if (airportOnly) list = list.filter((c) => c.offers_airport_service);
    if (make !== "All") list = list.filter((c) => c.make === make);
    if (model !== "All") list = list.filter((c) => carModel(c) === model);
    const sorted = [...list];
    if (sort === "price_asc") sorted.sort((a, b) => carPrice(a) - carPrice(b));
    else if (sort === "price_desc") sorted.sort((a, b) => carPrice(b) - carPrice(a));
    else if (sort === "near" && userPos) sorted.sort((a, b) => distTo(a) - distTo(b));
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCars, active, make, model, sort, userPos, airportOnly]);
  const carQuery = filtered && start && end
    ? `?start=${start}&end=${end}&st=${encodeURIComponent(startTime)}&et=${encodeURIComponent(endTime)}${loc ? `&loc=${encodeURIComponent(loc)}` : ""}`
    : (loc ? `?loc=${encodeURIComponent(loc)}` : "");

  return (
    <div data-testid="fleet-page" className="bg-white min-h-screen pt-32 pb-24 px-5 sm:px-8">
      <Seo
        title="Affordable Rental Cars in Boston & MA | Belo Rentals Fleet"
        description="Browse Belo Rentals' professional fleet available in Boston with Logan Airport pickup & drop-off. Economy cars and SUVs from $50/day. Check availability and book online."
        path="/fleet"
        keywords="Boston car rental fleet, Logan Airport car rental, SUV rental Boston Logan, economy car rental Boston, airport car rental Boston"
      />
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 tracking-tight font-bold">Car rental marketplace</h1>
          <p className="text-slate-500 mt-3 max-w-xl">Pick your dates and location to see real-time availability across our Boston &amp; MA fleet.</p>
        </div>
        <div data-testid="availability-filter" className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-sm shadow-slate-200/60">
          <p className="flex items-center gap-2 label-caps text-slate-500 mb-5"><span className="grid place-items-center w-7 h-7 rounded-full bg-blue-50 text-blue-600"><CalendarDays size={15} /></span> {t("fleet.checkAvailability")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3.5 items-end">
            <div>
              <span className="text-xs font-semibold text-slate-500 block mb-1.5">{t("fleet.pickupDate")}</span>
              <DateField value={start} onChange={setStart} fromDate={new Date()} placeholder={t("fleet.pickup")} testid="fleet-start-date" />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-500 block mb-1.5">{t("fleet.pickupTime")}</span>
              <select value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="fleet-start-time" className="belo-input cursor-pointer">
                {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
              </select>
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-500 block mb-1.5">{t("fleet.returnDate")}</span>
              <DateField value={end} onChange={setEnd} fromDate={start ? addDays(new Date(start), 1) : addDays(new Date(), 1)} placeholder={t("fleet.return")} testid="fleet-end-date" />
            </div>
            <div>
              <span className="text-xs font-semibold text-slate-500 block mb-1.5">{t("fleet.returnTime")}</span>
              <select value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="fleet-end-time" className="belo-input cursor-pointer">
                {TIMES.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={checkAvailability} disabled={!canSearch} data-testid="fleet-search-btn" className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40 shadow-sm shadow-blue-600/25">
                <Search size={16} /> {t("fleet.search")}
              </button>
              {filtered && (
                <button onClick={clearFilter} data-testid="fleet-clear-btn" title={t("fleet.clear")} className="grid place-items-center px-4 border border-slate-200 text-slate-600 rounded-full hover:bg-slate-50 transition-colors"><X size={16} /></button>
              )}
            </div>
          </div>
          {filtered && start && end && (
            <p data-testid="fleet-window-label" className="text-sm text-slate-600 mt-5 pt-4 border-t border-slate-100">
              {t("fleet.showing")} <b>{format(new Date(start), "MMM d")}, {startTime}</b> → <b>{format(new Date(end), "MMM d")}, {endTime}</b>
            </p>
          )}
          <div className="mt-5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setAirportOnly((v) => !v)}
              data-testid="fleet-airport-toggle"
              aria-pressed={airportOnly}
              className={`inline-flex items-center gap-2.5 rounded-full border-2 pl-2 pr-4 py-1.5 text-sm font-semibold transition-colors ${airportOnly ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            >
              <span className={`grid place-items-center w-7 h-7 rounded-full ${airportOnly ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><Plane size={14} /></span>
              Logan Airport pickup &amp; return
              <span className={`w-9 h-5 rounded-full relative transition-colors ${airportOnly ? "bg-blue-600" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${airportOnly ? "left-4" : "left-0.5"}`} />
              </span>
            </button>
            {airportOnly && <p className="text-xs text-slate-500 mt-2" data-testid="fleet-airport-note">Showing only vehicles from hosts who offer Logan Airport pickup &amp; return.</p>}
          </div>
        </div>

        {categories.length > 2 && (
          <div className="flex flex-wrap gap-3 mt-8">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  active === c ? "bg-blue-600 text-white shadow-sm shadow-blue-600/20" : "bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300"
                }`}
              >
                {c === "All" ? t("fleet.all") : c}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-8 flex-wrap">
          <p className="text-sm text-slate-500">{cars.length} {cars.length === 1 ? t("fleet.vehicle") : t("fleet.vehicles")}</p>
          <div className="flex items-center gap-3 flex-wrap">
            {filtered && (
              <div data-testid="make-model-filter" className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Make</span>
                  <select value={make} onChange={(e) => setMake(e.target.value)} data-testid="fleet-make" className="belo-input py-2 cursor-pointer">
                    {makes.map((m) => <option key={m} value={m}>{m === "All" ? "All makes" : m}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Model</span>
                  <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="fleet-model" className="belo-input py-2 cursor-pointer">
                    {models.map((m) => <option key={m} value={m}>{m === "All" ? "All models" : m}</option>)}
                  </select>
                </label>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">{t("fleet.sortBy")}</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="fleet-sort" className="belo-input py-2 cursor-pointer">
                <option value="price_asc">{t("fleet.sortPriceAsc")}</option>
                <option value="price_desc">{t("fleet.sortPriceDesc")}</option>
                <option value="near">{t("fleet.sortNearest")}</option>
              </select>
            </label>
          </div>
        </div>
        {sort === "near" && (
          <p data-testid="fleet-geo-status" className="text-xs text-slate-400 mt-2">
            {geoState === "locating" && t("fleet.geoLocating")}
            {geoState === "ok" && t("fleet.geoOk")}
            {(geoState === "denied" || geoState === "unavailable") && t("fleet.geoDenied")}
          </p>
        )}

        {loading ? (
          <p className="text-slate-400 mt-16">{t("fleet.loading")}</p>
        ) : cars.length === 0 ? (
          <p data-testid="fleet-empty" className="text-slate-400 mt-16">
            {filtered ? t("fleet.emptyFiltered") : t("fleet.emptyCategory")}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {cars.map((car, i) => <CarCard key={car.id} car={car} index={i} query={carQuery} />)}
          </div>
        )}
      </div>
    </div>
  );
}
